import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

export default function ArchiveList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState([]);
  const [archiveList, setArchiveList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.ARCHIVE_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isArchiveListLoading, setIsArchiveListLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { creatorList, fetchSearchInfo } = useSearchInfo();

  useEffect(() => {
    fetchData();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  const columns = ["archiveSeq", "title", "viewCnt", "createrName", "createdAt"];

  const columnConfig = {
    archiveSeq: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.archive-seq"),
    },
    title: {
      style: {
        width: "60%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.title"),
    },
    viewCnt: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.view-count"),
    },

    createrName: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        width: "12%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("bulletin.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  const searchFields = [
    {
      name: "title",
      label: t("bulletin.title"),
      placeholder: t("bulletin.title-placeholder"),
    },
    {
      name: "createrId",
      label: t("bulletin.creator"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("bulletin.creator-placeholder"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const handleDeleteArchive = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.deleteArchive,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.ARCHIVES, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        showSuccessToast(t("bulletin.archive-delete-success", { count: selectedRows.length }));
        setSelectedRows([]);
        queryClient.invalidateQueries(queryKeys.getArchiveList);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        showErrorToast(t("bulletin.archive-delete-error"));
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      await deleteData.mutateAsync(bodyRequest);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting archives:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddArchive = () => {
    navigate("/archive/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.archiveSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const getListData = async (bodyRequest = {}) => {
    setIsArchiveListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.ARCHIVES, {
        pageSize,
        startRow,
        filters,
        sortKey: "archiveSeq",
      });

      if (response && typeof response !== "string") {
        setArchiveList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching archives from IndexedDB:", error);
      showErrorToast(t("bulletin.get-archive-list-error"));
    } finally {
      setIsArchiveListLoading(false);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="archive-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="archive-list-header">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}
            data-testid="archive-list-title"
          >
            {uppercase(t("bulletin.archive-list-title"))}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="archive-list-controls">
        <TableHeaderControls
          controlsTestId="archive-list-table-header-controls"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDeleteArchive}
          onAdd={handleAddArchive}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("bulletin.add-archive-button")}
          titleDeleteButton={t("bulletin.delete-archive-button")}
          isDisplayDeleteButton={hasDeletePermission}
          searchInputTestId="archive-list-search-input"
          addButtonTestId="archive-list-add-button"
          deleteButtonTestId="archive-list-delete-button"
          advancedSearchButtonTestId="archive-list-advanced-search-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="archive-list-advanced-search">
          <AdvancedSearch
            data-testid="archive-list-advanced-search-panel"
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Archive Table */}
      <div className="flex-grow min-h-0" data-testid="archive-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={archiveList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isArchiveListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="archiveSeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="archive-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("bulletin.delete-archive-button"))}
        description={t("bulletin.delete-archive-warning", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
}
